import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarMonthIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Folder as FolderIcon,
  SupportAgent as SupportAgentIcon,
  MenuBook as MenuBookIcon,
  Email as EmailIcon,
  RealEstateAgent as RealEstateAgentIcon,
  TrendingUp as TrendingUpIcon,
  ChangeCircle as ChangeCircleIcon,
} from "@mui/icons-material";
import { logout } from "../../store/slices/authSlice";
import MDBox from "../../components/MDBox";
import { getCurrentUser } from "../../services/auth";

const drawerWidth = 240;

const DashboardLayout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [userLetter, setUserLetter] = useState("U");

  useEffect(() => {
    async function fetchUser() {
      const user = await getCurrentUser();
      const letter = user?.username
        ? user.username[0].toUpperCase()
        : user?.name
          ? user.name[0].toUpperCase()
          : user?.email
            ? user.email[0].toUpperCase()
            : "U";
      setUserLetter(letter);
    }
    fetchUser();
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout());
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const menuItems = [
    {
      text: "Aufgaben",
      icon: <DashboardIcon />,
      path: "/dashboard",
    },
    {
      text: "Kalender",
      icon: <CalendarMonthIcon />,
      path: "/calendar",
    },
    {
      text: "E-Mails",
      icon: <EmailIcon />,
      path: "/emails",
    },
    {
      text: "Vermögensübersicht & Kontakte",
      icon: <RealEstateAgentIcon />,
      path: "/asset-overview",
    },
    {
      text: "Mieterwechsel",
      icon: <ChangeCircleIcon />,
      path: "/tenant-changes",
    },
    {
      text: "Mieterhöhungen",
      icon: <TrendingUpIcon />,
      path: "/rent-increases",
    },
    {
      text: "Dateimanager",
      icon: <FolderIcon />,
      path: import.meta.env.VITE_FILE_MANAGER_URL,
      external: true,
    },
  ];

  const getPageTitle = () => {
    switch (location.pathname) {
      case "/dashboard":
        return "Seiten / Dashboard";
      case "/calendar":
        return "Seiten / Kalender";
      case "/emails":
        return "Seiten / E-Mails";
      case "/tenant-changes":
        return "Seiten / Mieterwechsel";
      case "/rent-increases":
        return "Seiten / Mieterhöhungen";
      case "/asset-overview":
        return "Seiten / Vermögensübersicht & Kontakte";
      case "/settings":
        return "Seiten / Einstellungen";
      case "/support":
        return "Seiten / Support";
      default:
        return "Seiten / Dashboard";
    }
  };

  const drawer = (
    <Box>
      <Box sx={{ p: 2, textAlign: "center" }}>
        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#344767" }}>
          Geschwister Welsch Dashboard
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              component={item.external ? "a" : Link}
              to={!item.external ? item.path : undefined}
              href={item.external ? item.path : undefined}
              target={item.external ? "_blank" : undefined}
              rel={item.external ? "noopener noreferrer" : undefined}
              onClick={() => isMobile && setMobileOpen(false)}
              sx={{
                mx: 2,
                borderRadius: "0.5rem",
                backgroundColor:
                  location.pathname === item.path ? "#f0f2f5" : "transparent",
                "&:hover": {
                  backgroundColor: "#f8f9fa",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: "2rem",
                  color:
                    location.pathname === item.path ? "#1976d2" : "#67748e",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.875rem",
                    fontWeight: location.pathname === item.path ? 500 : 400,
                    color:
                      location.pathname === item.path ? "#1976d2" : "#67748e",
                  },
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider sx={{ mt: "auto" }} />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            component="a"
            href={import.meta.env.VITE_DOCS_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: "#67748e",
              }}
            >
              <MenuBookIcon />
            </ListItemIcon>
            <ListItemText
              primary="Dokumentation"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: 400,
                  color: "#67748e",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/support"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor:
                location.pathname === "/support" ? "#f0f2f5" : "transparent",
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color: location.pathname === "/support" ? "#1976d2" : "#67748e",
              }}
            >
              <SupportAgentIcon />
            </ListItemIcon>
            <ListItemText
              primary="Support"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: location.pathname === "/support" ? 500 : 400,
                  color:
                    location.pathname === "/support" ? "#1976d2" : "#67748e",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/settings"
            onClick={() => isMobile && setMobileOpen(false)}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              backgroundColor:
                location.pathname === "/settings" ? "#f0f2f5" : "transparent",
              "&:hover": {
                backgroundColor: "#f8f9fa",
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: "2rem",
                color:
                  location.pathname === "/settings" ? "#1976d2" : "#67748e",
              }}
            >
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText
              primary="Einstellungen"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  fontWeight: location.pathname === "/settings" ? 500 : 400,
                  color:
                    location.pathname === "/settings" ? "#1976d2" : "#67748e",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              mx: 2,
              borderRadius: "0.5rem",
              "&:hover": {
                backgroundColor: "#ffebee",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: "2rem", color: "#f44336" }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Abmelden"
              sx={{
                "& .MuiListItemText-primary": {
                  fontSize: "0.875rem",
                  color: "#f44336",
                },
              }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: "flex" }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth + 36}px)` },
          ml: { md: `${drawerWidth}px` },
          boxShadow: "none",
          borderRadius: "0.75rem",
          margin: "18px",
          backdropFilter: "saturate(200%) blur(30px)",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" }, color: "#344767" }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            sx={{
              flexGrow: 1,
              color: "#344767",
              fontWeight: "bold",
            }}
          >
            {getPageTitle()}
          </Typography>
          <IconButton onClick={() => navigate("/settings")} sx={{ ml: 2 }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#1976d2",
                cursor: "pointer",
              }}
            >
              {userLetter}
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant={isMobile ? "temporary" : "permanent"}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
              backgroundColor: "#fff",
              border: "none",
              boxShadow: "0rem 1.25rem 1.6875rem 0rem rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <Toolbar />
        <MDBox py={3}>{children}</MDBox>
      </Box>
    </Box>
  );
};

export default DashboardLayout;
